import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Home, QrCode, AlertTriangle, User, Calendar, Bell, Activity, ArrowLeftRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';

export default function MobileLayout({ children }) {
  const { user, viewMode, toggleViewMode } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const watchIdRef = useRef(null);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const data = await api.getUnreadCount();
        setUnreadCount(data.count || 0);
      } catch (error) {
        console.error('Error fetching notification count:', error);
      }
    };
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Continuous location tracking for guards
  useEffect(() => {
    if (!user || user.role !== 'guard') return;
    
    const startTracking = () => {
      if (!navigator.geolocation) return;
      
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          api.updateLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, timeout: 10000 }
      );
      
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          api.updateLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 60000, timeout: 30000 }
      );
    };
    
    startTracking();
    
    const interval = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          api.updateLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }, 60000);
    
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      clearInterval(interval);
    };
  }, [user]);

  const isSupervisor = user?.role === 'supervisor';

  const handleToggle = () => {
    const newMode = toggleViewMode();
    navigate(newMode === 'field' ? '/dashboard' : '/admin');
  };

  const guardNavItems = [
    { path: '/dashboard', icon: Home, label: 'Home' },
    { path: '/schedule', icon: Calendar, label: 'Schedule' },
    { path: '/patrol', icon: QrCode, label: 'Patrol' },
    { path: '/incidents', icon: AlertTriangle, label: 'Report' },
    { path: '/profile', icon: User, label: 'Profile' },
  ];

  const navItems = guardNavItems;

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-slate-200 z-40">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">SG</span>
            </div>
            <span className="font-semibold text-slate-800 text-sm">Security Guard</span>
          </div>
          <div className="flex items-center gap-1">
            {isSupervisor && (
              <button
                onClick={handleToggle}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-colors"
                data-testid="switch-to-manager-btn"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" />
                Manager
              </button>
            )}
            <button
              onClick={() => navigate('/profile')}
              className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
              data-testid="notification-bell"
            >
              <Bell className="w-5 h-5 text-slate-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-lg mx-auto">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 mobile-nav z-50">
        <div className="max-w-lg mx-auto flex justify-around items-center py-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/admin'}
              className={({ isActive }) =>
                `flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
                  isActive
                    ? 'text-blue-700 bg-blue-50'
                    : 'text-slate-500 hover:text-slate-700'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs mt-1 font-medium">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
